import { api } from '../lib/api.js';

export type UserDeviceStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVOKED';

export interface UserDevice {
  id: string;
  userId: string;
  userEmail: string | null;
  userLabel: string | null;
  deviceId: string;
  label: string | null;
  deviceType: string;
  userAgent: string | null;
  ipAddress: string | null;
  status: UserDeviceStatus;
  reviewedById: string | null;
  reviewedByLabel: string | null;
  reviewNotes: string | null;
  reviewedAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string | null;
  /**
   * Is this person signed in on this browser RIGHT NOW.
   *
   * The server reads it from an unrevoked, unexpired `RefreshToken` bound to
   * this exact (userId, deviceId) pair — the only row in the schema that means
   * a live login. It is NOT `lastSeenAt` (the last time this browser reached
   * the door, including the times it was turned away) and NOT the APPROVED
   * verdict (permission to sign in, not the act of it).
   *
   * `false` is an answer, not a gap: the session query runs for every listed
   * row, so the client may state "not signed in" rather than staying silent.
   * Present on the LIST only — the approve/reject/revoke replies return the
   * bare record, which is why every mutation here re-reads the list.
   */
  hasLiveSession: boolean;
}

export interface UserDevicePage {
  data: UserDevice[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** The one query key the pending badge reads, shared so nothing hand-writes it. */
export const PENDING_DEVICE_COUNT_QUERY_KEY = [
  'device-approvals',
  'pending-count',
] as const;

export const deviceApprovalsApi = {
  list: (params: {
    status?: UserDeviceStatus;
    /** Server-side; matches the device's columns and the person signed in on it. */
    search?: string;
    page?: number;
    pageSize?: number;
  }) =>
    api
      .get<{ data: UserDevicePage }>('/device-approvals', { params })
      .then((r) => r.data),
  pendingCount: () =>
    api
      .get<{ data: { count: number } }>('/device-approvals/pending-count')
      .then((r) => r.data.count),
  approve: (id: string) =>
    api.post<{ data: UserDevice }>(`/device-approvals/${id}/approve`, {}),
  // The note is required by the server, so it is required here — a client that
  // let the field be empty would only turn a clear rule into a 422.
  reject: (id: string, note: string) =>
    api.post<{ data: UserDevice }>(`/device-approvals/${id}/reject`, { note }),
  revoke: (id: string, note?: string) =>
    api.post<{ data: UserDevice }>(`/device-approvals/${id}/revoke`, note ? { note } : {}),
};
